import Tebex from '@tebexio/tebex.js'
import { appearance, isSmallScreen, lockScroll, tebexLocale } from './embed'
import type { Basket } from './types'

// tebex checkout is a global singleton, so only the listeners of the latest launch may stay active
let listeners: (() => void)[] = []
// tebex.js throws while its lightbox is still opening or open, which must not end up in the fallback (e.g. on a double
// click, or on enter while the checkout button still has the focus)
let launching = false

export function useTebexCheckout() {
  const { localeProperties } = useI18n()
  const { abandonBasket, refreshBasket } = useTebex()

  const openHostedCheckout = async (basket: Basket) => {
    if (basket.links?.checkout) {
      await navigateTo(basket.links.checkout, { external: true })
    }
  }

  const launchCheckout = async (basket: Basket) => {
    if (launching || Tebex.checkout.isOpen) {
      return
    }

    // small screens use the hosted checkout in the same tab, it returns to the shop through the basket's complete_url
    if (isSmallScreen()) {
      return openHostedCheckout(basket)
    }

    Tebex.checkout.init({
      ident: basket.ident,
      locale: tebexLocale(localeProperties.value.language),
      ...appearance,
    })

    listeners.forEach(unsubscribe => unsubscribe())
    listeners = [
      // only clears our copy of the basket, tebex confirms the payment and delivers the packages on its own
      Tebex.checkout.on('payment:complete', () => abandonBasket()),
      Tebex.checkout.on('close', () => {
        lockScroll(false)
        refreshBasket()
          .catch(e => console.warn('failed to refresh tebex basket after checkout', e))
      }),
    ]

    launching = true
    // the lightbox shows right away, even while the checkout is still loading
    lockScroll(true)
    try {
      await Tebex.checkout.launch()
    }
    catch (e) {
      console.warn('failed to launch tebex checkout, falling back to hosted checkout', e)
      lockScroll(false)
      await openHostedCheckout(basket)
    }
    finally {
      launching = false
    }
  }

  return { launchCheckout }
}
